import { Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Stack, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { LayoutGrid, Users, Settings, Info, Megaphone, Star, HelpCircle, LogOut, Gift } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const DRAWER_WIDTH = 256;

const StyledDrawer = styled(Drawer)(() => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box',
    border: 'none',
    borderRight: '1px solid #E5E7EB',
    bgcolor: 'white'
  }
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2, 3),
  marginBottom: theme.spacing(4)
}));

const StyledListItemButton = styled(ListItemButton)<{ active?: boolean }>(({ theme, active }) => ({
  borderRadius: '8px',
  marginBottom: theme.spacing(0.5),
  padding: '12px 16px',
  backgroundColor: active ? '#EEF2FF' : 'transparent',
  color: active ? '#4F46E5' : '#6B7280',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: active ? '#E0E7FF' : '#F3F4F6',
    color: active ? '#4338CA' : '#111827'
  },
  '& .MuiListItemIcon-root': {
    color: active ? '#4F46E5' : '#6B7280',
    minWidth: 36
  },
  '& .MuiListItemText-primary': {
    fontSize: '0.875rem',
    fontWeight: active ? 600 : 500
  }
}));

interface SidebarUser {
  name: string;
  businessName: string;
  avatar: string;
  businessLogo?: string | null;
  usagePlan?: string;
}

interface SidebarProps {
  user?: SidebarUser;
}

const menuItems = [
  { icon: LayoutGrid, label: 'Dashboard', path: '/dashboard' },
  { icon: Megaphone, label: 'Campaigns', path: '/campaigns' },
  { icon: Star, label: 'Reviews', path: '/reviews' },
  { icon: Gift, label: 'Rewards', path: '/rewards' },
  { icon: Users, label: 'Customers', path: '/customers' },
  { icon: Settings, label: 'Settings', path: '/settings' }
];

const bottomMenuItems = [
  { icon: HelpCircle, label: 'Help Center', path: '/help' },
  { icon: LogOut, label: 'Log Out', path: '/signin' }
];

const Sidebar = ({ user }: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <StyledDrawer variant="permanent" anchor="left">
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
        <LogoContainer>
          {user?.businessLogo ? (
            <Box
              component="img"
              src={user.businessLogo}
              alt={user.businessName || 'Business Logo'}
              sx={{
                width: 48,
                height: 48,
                objectFit: 'contain',
                borderRadius: '8px'
              }}
              onError={(e) => {
                // Fallback to default icon if image fails to load
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <Box
              sx={{
                width: 48,
                height: 48,
                bgcolor: '#EEF2FF',
                color: '#4F46E5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px'
              }}
            >
              <Info size={28} />
            </Box>
          )}
          <Stack spacing={0}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: '1rem',
                letterSpacing: '-0.01em',
                lineHeight: 1.3
              }}
            >
              {user?.businessName || 'Business Name'}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
                lineHeight: 1.4
              }}
            >
              {user?.usagePlan || 'Starter Plan'}
            </Typography>
          </Stack>
        </LogoContainer>

        <List sx={{ flex: 1, px: 1 }}>
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <ListItem key={item.path} disablePadding>
                <StyledListItemButton
                  active={isActive}
                  onClick={() => navigate(item.path)}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                    <Icon size={20} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: isActive ? 700 : 500
                    }}
                  />
                </StyledListItemButton>
              </ListItem>
            );
          })}
        </List>

        <Box sx={{ px: 1 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => navigate('/campaigns/create')}
            sx={{
              height: 44,
              backgroundColor: '#4F46E5',
              fontWeight: 600,
              fontSize: '0.875rem',
              textTransform: 'none',
              borderRadius: '8px',
              mb: 3,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#4338CA',
                boxShadow: 'none'
              }
            }}
          >
            + Create Campaign
          </Button>

          <Box sx={{ borderTop: '1px solid #E5E7EB', pt: 2 }}>
            <List sx={{ p: 0 }}>
              {bottomMenuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                    <StyledListItemButton
                      onClick={() => navigate(item.path)}
                    >
                      <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                        <Icon size={20} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: 500
                        }}
                      />
                    </StyledListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Box>
      </Box>
    </StyledDrawer>
  );
};

export default Sidebar;